<template>
  <div class="q-pa-md relative-position" style="min-height: 220px;">
    <q-inner-loading :showing="loading">
      <loading-spinner />
    </q-inner-loading>

    <div
      v-if="showQuickActions"
      class="q-mb-md"
    >
      <q-tabs
        shrink
        outside-arrows
        mobile-arrows
        :breakpoint="0"
      >
        <stacked-btn
          v-if="can(permissions.canAddInvoices)"
          bubble
          icon="mdi-cash"
          label="New Invoice"
          style="width: 100px;"
          class="full-height"
          @click="$emit('newInvoice')"
        />
        <stacked-btn
          v-if="can(permissions.canAddQuotations)"
          bubble
          icon="mdi-file-document"
          label="New Quote"
          style="width: 100px;"
          class="full-height"
          @click="$emit('newQuotation')"
        />
        <stacked-btn
          v-if="can(permissions.canManagePayments)"
          bubble
          icon="mdi-cash-multiple"
          label="Take Payment"
          color="green-7"
          style="width: 100px;"
          class="full-height"
          @click="$refs.invoiceActions.takePayment()"
        />
        <stacked-btn
          v-if="can(permissions.canManagePayments)"
          bubble
          icon="mdi-bell-outline"
          label="Send Reminder"
          color="teal-8"
          style="width: 100px;"
          class="full-height"
          @click="$refs.invoiceActions.sendReminder()"
        />
      </q-tabs>
      <q-separator class="q-mt-sm" />
    </div>

    <template v-if="!loading">
      <no-results
        v-if="isEmpty"
        icon="mdi-receipt-text-outline"
        title="No billing records"
        subtitle="Invoices, quotations and payments for this client will appear here."
        class="full-width"
      />

      <template v-else>
        <q-tabs
          v-model="billingTab"
          dense
          no-caps
          align="left"
          active-color="blue"
          indicator-color="blue"
          class="text-grey-7"
        >
          <q-tab
            name="invoices"
            :label="invoices.length ? 'Invoices (' + invoices.length + ')' : 'Invoices'"
          />
          <q-tab
            name="quotations"
            :label="quotations.length ? 'Quotations (' + quotations.length + ')' : 'Quotations'"
          />
          <q-tab
            v-if="canViewPayments"
            name="payments"
            :label="payments.length ? 'Payments (' + payments.length + ')' : 'Payments'"
          />
        </q-tabs>
        <q-separator class="q-mb-md" />

        <q-tab-panels
          v-model="billingTab"
          class="overflow-panels"
        >
          <!-- INVOICES -->
          <q-tab-panel
            name="invoices"
            class="q-pa-none"
          >
            <no-results
              v-if="invoices.length === 0"
              icon="mdi-receipt-text-outline"
              title="No invoices"
              class="full-width"
            />
            <template v-else>
              <q-input
                v-if="invoices.length > 5"
                v-model="invoiceFilter"
                dense
                outlined
                clearable
                debounce="200"
                placeholder="Filter invoices..."
                class="q-mb-md"
              >
                <template #prepend>
                  <q-icon name="mdi-magnify" />
                </template>
              </q-input>

              <q-item-label
                v-if="invoiceFilter && filteredInvoices.length === 0"
                caption
                class="q-py-md"
              >
                No invoices match "{{ invoiceFilter }}".
              </q-item-label>

              <q-list
                v-if="filteredInvoices.length"
                bordered
                separator
                class="rounded-borders"
              >
                <q-item
                  v-for="invoice in filteredInvoices"
                  :key="'invoice-' + invoice.id"
                  clickable
                  class="q-py-md"
                  @click="goTo('invoicing/view?invoice_id=' + invoice.id)"
                >
                  <q-item-section>
                    <q-item-label class="text-weight-medium text-blue-8">
                      {{ invoice.invoice_number }}
                    </q-item-label>
                    <q-item-label
                      v-if="invoice.client"
                      caption
                      lines="1"
                    >
                      {{ invoice.client.display_name }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section side class="gt-xs">
                    <InvoiceStatus :invoice="invoice" />
                  </q-item-section>

                  <q-item-section
                    side
                    class="text-right"
                    style="min-width: 78px;"
                  >
                    <q-item-label class="text-weight-bold">
                      {{ friendlyNumber(invoice.total, true) }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section
                    v-if="can(permissions.canManagePayments)"
                    side
                    style="min-width: 90px;"
                  >
                    <div class="row items-center justify-end no-wrap q-gutter-xs">
                      <q-btn
                        v-if="canTakePayment(invoice)"
                        dense
                        flat
                        round
                        icon="mdi-cash-multiple"
                        color="green-7"
                        @click.stop="$refs.invoiceActions.takePayment(invoice.id)"
                      >
                        <q-tooltip>Take payment</q-tooltip>
                      </q-btn>
                      <q-btn
                        v-if="canSendReminder(invoice)"
                        dense
                        flat
                        round
                        icon="mdi-bell-outline"
                        color="teal-8"
                        @click.stop="$refs.invoiceActions.sendReminder(invoice.id)"
                      >
                        <q-tooltip>Send payment reminder</q-tooltip>
                      </q-btn>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </q-tab-panel>

          <!-- QUOTATIONS -->
          <q-tab-panel
            name="quotations"
            class="q-pa-none"
          >
            <no-results
              v-if="quotations.length === 0"
              icon="mdi-file-document-outline"
              title="No quotations"
              class="full-width"
            />
            <template v-else>
              <q-input
                v-if="quotations.length > 5"
                v-model="quotationFilter"
                dense
                outlined
                clearable
                debounce="200"
                placeholder="Filter quotations..."
                class="q-mb-md"
              >
                <template #prepend>
                  <q-icon name="mdi-magnify" />
                </template>
              </q-input>

              <q-item-label
                v-if="quotationFilter && filteredQuotations.length === 0"
                caption
                class="q-py-md"
              >
                No quotations match "{{ quotationFilter }}".
              </q-item-label>

              <q-list
                v-if="filteredQuotations.length"
                bordered
                separator
                class="rounded-borders"
              >
                <q-item
                  v-for="quotation in filteredQuotations"
                  :key="'quotation-' + quotation.id"
                  clickable
                  class="q-py-md"
                  @click="$emit('viewQuotation', quotation.id)"
                >
                  <q-item-section>
                    <q-item-label class="text-weight-medium text-blue-8">
                      {{ quotation.quotation_number }}
                    </q-item-label>
                    <q-item-label
                      v-if="quotation.client"
                      caption
                      lines="1"
                    >
                      {{ quotation.client.display_name }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section side class="gt-xs">
                    <QuotationStatus :quotation="quotation" />
                  </q-item-section>

                  <q-item-section
                    side
                    class="text-right"
                    style="min-width: 78px;"
                  >
                    <q-item-label class="text-weight-bold">
                      {{ friendlyNumber(quotation.total, true) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </q-tab-panel>

          <!-- PAYMENTS -->
          <q-tab-panel
            v-if="canViewPayments"
            name="payments"
            class="q-pa-none"
          >
            <no-results
              v-if="payments.length === 0"
              icon="mdi-cash-multiple"
              title="No payments"
              class="full-width"
            />
            <template v-else>
              <q-input
                v-if="payments.length > 5"
                v-model="paymentFilter"
                dense
                outlined
                clearable
                debounce="200"
                placeholder="Filter payments..."
                class="q-mb-md"
              >
                <template #prepend>
                  <q-icon name="mdi-magnify" />
                </template>
              </q-input>

              <q-item-label
                v-if="paymentFilter && filteredPayments.length === 0"
                caption
                class="q-py-md"
              >
                No payments match "{{ paymentFilter }}".
              </q-item-label>

              <q-list
                v-if="filteredPayments.length"
                bordered
                separator
                class="rounded-borders"
              >
                <q-item
                  v-for="payment in filteredPayments"
                  :key="'payment-' + payment.id"
                  class="q-py-md"
                >
                  <q-item-section side>
                    <date :date="payment.date" />
                  </q-item-section>

                  <q-item-section>
                    <q-item-label v-if="payment.description">
                      {{ payment.description }}
                    </q-item-label>
                    <q-item-label
                      v-else
                      class="opaque-50"
                    >
                      Description not specified
                    </q-item-label>
                    <q-item-label
                      v-if="payment.invoice"
                      caption
                    >
                      <q-icon
                        name="mdi-receipt-text-outline"
                        size="14px"
                        class="q-mr-xs"
                      />
                      <basic-link @click="goTo('invoicing/view?invoice_id=' + payment.invoice.id)">
                        {{ payment.invoice.invoice_number }}
                      </basic-link>
                    </q-item-label>
                    <q-item-label
                      v-if="payment.paid_via"
                      caption
                      class="text-grey-7"
                    >
                      {{ payment.paid_via }}
                    </q-item-label>
                  </q-item-section>

                  <q-item-section
                    side
                    class="text-weight-bold text-green-8"
                    style="min-width: 90px;"
                  >
                    {{ friendlyNumber(payment.amount, true) }}
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </q-tab-panel>
        </q-tab-panels>
      </template>
    </template>

    <InvoiceActions
      ref="invoiceActions"
      :client-id="client.id"
      @updated="onPaid"
    />
  </div>
</template>

<script>
import InvoiceStatus from 'components/app/invoicing/partials/Status'
import QuotationStatus from 'components/app/quotations/partials/Status'
import InvoiceActions from 'components/app/calendar/partials/view/panels/partials/InvoiceActions'

/*
  Surfaces a client's invoices, quotations and payments with direct "view",
  "take payment" and "send reminder" actions, mirroring the appointment Billing
  panel. Lists come from the per-client paginate endpoints; the full invoice is
  fetched on demand (inside InvoiceActions) for payment/reminder dialogs.
*/
export default {
  components: {
    InvoiceStatus,
    QuotationStatus,
    InvoiceActions
  },
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  emits: ['updated', 'newInvoice', 'newQuotation', 'viewQuotation'],
  data () {
    return {
      loading: false,
      invoices: [],
      quotations: [],
      payments: [],
      billingTab: 'invoices',
      invoiceFilter: '',
      quotationFilter: '',
      paymentFilter: ''
    }
  },
  computed: {
    canViewPayments () {
      return this.can(this.permissions.canManagePayments)
    },
    showQuickActions () {
      return this.can(this.permissions.canAddInvoices) ||
        this.can(this.permissions.canAddQuotations) ||
        this.can(this.permissions.canManagePayments)
    },
    isEmpty () {
      return this.invoices.length === 0 &&
        this.quotations.length === 0 &&
        !(this.canViewPayments && this.payments.length)
    },
    filteredInvoices () {
      return this.filterList(this.invoices, this.invoiceFilter, i => [i.invoice_number, i.client && i.client.display_name])
    },
    filteredQuotations () {
      return this.filterList(this.quotations, this.quotationFilter, q => [q.quotation_number, q.client && q.client.display_name])
    },
    filteredPayments () {
      return this.filterList(this.payments, this.paymentFilter, p => [p.description, p.invoice && p.invoice.invoice_number])
    }
  },
  methods: {
    async loadBilling () {
      this.loading = true
      const requests = [
        this.$get('v1/invoice/paginate', {
          clients: [this.client.id], page: 1, rows: 50, sortBy: 'invoice_date', sortDirection: 'desc', filter: '', status: null, archived: 0
        }),
        this.$get('v1/quotation/paginate', {
          clients: [this.client.id], page: 1, rows: 50, sortBy: 'quotation_date', sortDirection: 'desc', filter: '', status: null, archived: 0
        })
      ]
      if (this.canViewPayments) {
        requests.push(this.$get('v1/payments/paginate', {
          clients: [this.client.id], page: 1, rows: 50, sortBy: 'date', sortDirection: 'desc', filter: '', archived: 0
        }))
      }
      const [invoiceRes, quotationRes, paymentRes] = await Promise.all(requests)
      this.loading = false
      if (invoiceRes.status === 'success') {
        this.invoices = invoiceRes.payload.invoices || []
      }
      if (quotationRes.status === 'success') {
        this.quotations = quotationRes.payload.quotations || []
      }
      if (paymentRes && paymentRes.status === 'success') {
        this.payments = paymentRes.payload.payments || []
      }
      // Open on whichever tab actually has something.
      if (!this.invoices.length && this.quotations.length) {
        this.billingTab = 'quotations'
      }
      else if (!this.invoices.length && !this.quotations.length && this.payments.length) {
        this.billingTab = 'payments'
      }
    },
    filterList (list, search, accessor) {
      const term = (search || '').toLowerCase().trim()
      if (!term) {
        return list
      }
      return list.filter(item =>
        accessor(item).some(value => (value || '').toLowerCase().includes(term))
      )
    },
    canTakePayment (invoice) {
      return this.can(this.permissions.canManagePayments) &&
        (invoice.status === 'OPEN' || invoice.status === 'PARTIAL')
    },
    canSendReminder (invoice) {
      return this.can(this.permissions.canManagePayments) &&
        (invoice.status === 'OPEN' || invoice.status === 'PARTIAL')
    },
    onPaid () {
      this.loadBilling()
      // Let the parent refresh any client-level billing badges.
      this.$emit('updated')
    }
  },
  mounted () {
    this.loadBilling()
    this.$bus.on('invoices:reload', this.loadBilling)
    this.$bus.on('payments:reload', this.loadBilling)
  },
  beforeUnmount () {
    this.$bus.off('invoices:reload', this.loadBilling)
    this.$bus.off('payments:reload', this.loadBilling)
  }
}
</script>
